import { ArrowLeft, Lock, Eye, Shield, Smartphone } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import welcomeBg from "../../assets/homebg.png";

const PrivacySecurityPage = () => {
  const navigate = useNavigate();
  const [faceId, setFaceId] = useState(false);
  const [fingerprint, setFingerprint] = useState(false);
  const [shareHealth, setShareHealth] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [activityStatus, setActivityStatus] = useState(false);

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera/profilepage")}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">
            Privacy & Security
          </h1>
          <div className="w-10" />
        </div>

        {/* Account Security */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="mb-4 flex items-center gap-3">
            <Lock className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-foreground">Account Security</h3>
          </div>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="h-auto w-full justify-start rounded-lg border-border py-3"
            >
              <div className="text-left">
                <div className="font-medium text-foreground">
                  Change Password
                </div>
                <div className="text-caption1 text-muted-foreground">
                  Last changed 3 months ago
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto w-full justify-start rounded-lg border-border py-3"
            >
              <div className="text-left">
                <div className="font-medium text-foreground">
                  Two-Factor Authentication
                </div>
                <div className="text-caption1 text-muted-foreground">
                  Add an extra layer of security
                </div>
              </div>
            </Button>
          </div>
        </Card>

        {/* Biometric */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="mb-4 flex items-center gap-3">
            <Smartphone className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-foreground">Biometric Lock</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-subhead font-medium text-foreground">
                  Face ID
                </div>
                <div className="text-caption1 text-muted-foreground">
                  Use Face ID to unlock
                </div>
              </div>
              <button
                onClick={() => setFaceId(!faceId)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  faceId ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                    faceId ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-subhead font-medium text-foreground">
                  Fingerprint
                </div>
                <div className="text-caption1 text-muted-foreground">
                  Use fingerprint to unlock
                </div>
              </div>
              <button
                onClick={() => setFingerprint(!fingerprint)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  fingerprint ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                    fingerprint ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </Card>

        {/* Privacy Settings */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="mb-4 flex items-center gap-3">
            <Eye className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-foreground">Privacy</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-subhead font-medium text-foreground">
                  Share health data
                </div>
                <div className="text-caption1 text-muted-foreground">
                  With healthcare providers
                </div>
              </div>
              <button
                onClick={() => setShareHealth(!shareHealth)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  shareHealth ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                    shareHealth ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-subhead font-medium text-foreground">
                  Analytics
                </div>
                <div className="text-caption1 text-muted-foreground">
                  Help improve the app
                </div>
              </div>
              <button
                onClick={() => setAnalytics(!analytics)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  analytics ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                    analytics ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-subhead font-medium text-foreground">
                  Activity status
                </div>
                <div className="text-caption1 text-muted-foreground">
                  Show when you&apos;re active
                </div>
              </div>
              <button
                onClick={() => setActivityStatus(!activityStatus)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  activityStatus ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
                    activityStatus ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </Card>

        {/* Data Management */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="mb-4 flex items-center gap-3">
            <Shield className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-foreground">Data Management</h3>
          </div>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg border-border"
            >
              Download my data
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg border-red-200 text-red-600"
            >
              Delete my account
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacySecurityPage;
